import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { ComponentType } from 'react';
import { applyTheme, readConfig, type WidgetConfig } from './theme.js';
import { useHeightReporter } from './height.js';
import './widget.css';

/**
 * Shared bootstrap for every widget entry point.
 *
 * Each widget's main.tsx is then three lines: import its component, call
 * mount(). The theme is applied before React renders so the first paint is
 * already in the broker's colours — applying it afterwards would flash white in
 * a dark-themed page.
 */
export interface MountOptions {
  /**
   * Zone used when the embed carries no `tz` parameter.
   *
   * The India corporate-event widgets pass 'Asia/Kolkata': their audience and
   * the exchanges they describe are both there, and a Mumbai broker should not
   * have to add a parameter to get IST. The global widgets leave it at UTC,
   * which is the only defensible neutral for an audience we cannot locate.
   */
  defaultTimeZone?: string;
}

export function mount(
  Widget: ComponentType<{ config: WidgetConfig }>,
  options: MountOptions = {},
): void {
  const config = readConfig({ defaultTimeZone: options.defaultTimeZone });
  applyTheme(config);

  const container = document.getElementById('root');
  if (!container) throw new Error('widget host element #root is missing');

  function Root() {
    // Every widget reports its height to the parent; doing it here rather than
    // in each widget means a new widget cannot forget to.
    useHeightReporter();
    return <Widget config={config} />;
  }

  createRoot(container).render(
    <StrictMode>
      <Root />
    </StrictMode>,
  );
}
